import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'react-toastify';
import {
  Loader2,
  Check,
  ShieldCheck,
  X,
  PenLine,
  Eye,
  ZoomIn,
  ZoomOut,
  ArrowRight,
  ChevronRight,
} from 'lucide-react';

import { cn } from '../../lib/utils';
import PublicLayout from '../layouts/PublicLayout';
import { loadPdfDocument, renderPageToImageUrl } from '../../lib/pdfRender';
import {
  useFetchSignSession,
  useSubmitSessionDocument,
  isRetryableSignError,
  SIGN_LOAD_TIMEOUT_MS,
} from '../../services/documents';
import {
  isAutoField,
  todayInCentral,
  LINE_HEIGHT_FACTOR,
  cleanDigitsValue,
  digitBoxLayout,
  digitsComplete,
  fieldEnabled,
  fieldRequired,
  nextFocusableFieldId,
} from './documentHelpers';
import SignatureModal from './SignatureModal';
import { GuideCard, buildGuideSteps, nextOpenStep, readingOrder } from './SignGuide';
import { loadSignDraft, saveSignDraft, clearSignDraft } from './signDraft';
import SignerErrorBoundary from './SignerErrorBoundary';

// Phones and small tablets fill through the guide card (Nic 2026-10-15).
const NARROW_MAX = 767;
// Touch-only screens fill through the card at any width (Nic 2026-10-24): an
// iPad or a phone turned sideways got the desktop page with small boxes, and
// rotating mid-sign swapped layouts under the signer.
const isTouchOnly = () => {
  try {
    return window.matchMedia('(hover: none) and (pointer: coarse)').matches;
  } catch (_) {
    return false;
  }
};
// A whole packet's page images share one memory budget on touch devices
// (Nic 2026-10-24): every page drawn at full phone density could pass 250 MB
// on a long agreement and make Safari reload the tab.
const TOUCH_PACKET_PIXELS = 40000000;
// The page-turn hint and field cues (Nic 2026-10-24): what the navigator tag
// says for each kind of step, like DocuSign's yellow tag.
const STEP_VERB = {
  signature: 'Sign',
  check: 'Check',
  choice: 'Choose',
};
const verbOf = (step) => STEP_VERB[step?.kind] || 'Fill in';

// RENDER_WIDTH is the desktop MAX — the actual base render width is responsive:
// min(720, measured container width), floored so fields stay tappable. Zoom
// multiplies from that responsive base.
const RENDER_WIDTH = 720;
const MIN_RENDER_WIDTH = 320;
// US-Letter aspect placeholder height for a given width (keeps the page
// footprint stable while the PDF loads so the layout doesn't jump).
const placeholderHeight = (w) => Math.round((w * 11) / 8.5);
const ZOOM_MIN = 1;
const ZOOM_MAX = 2.5;
const ZOOM_STEP = 0.25;
const EMPTY = {};
// Shown when the packet was voided or replaced while the signer was finishing.
const REPLACED_MSG = 'This agreement was replaced. Please use your newest link.';

// A single reused canvas context for measuring text width (shrink-to-fit).
let measureCtx = null;
const measureTextWidth = (text, fontPx) => {
  if (!measureCtx) measureCtx = document.createElement('canvas').getContext('2d');
  measureCtx.font = `${fontPx}px system-ui, -apple-system, "Segoe UI", sans-serif`;
  return measureCtx.measureText(text).width;
};

// Central, like the server's burned signing date (documentPdf formatSignDate),
// so the preview and the signed PDF never disagree by a day.
const todayStr = () => todayInCentral();

const isCheckableType = (p) => p.type === 'checkbox' || p.type === 'radio';
// Checkboxes stay square at any size (Nic 2026-10-15: tiny boxes on a phone
// rounded into circles, red ring included) and their check scales with the
// box (a fixed 12px icon overflowed and was clipped to a green blob).
const BOX_SQUARE = 'rounded-none';
const ScaledCheck = () => (
  <Check className="h-full max-h-4 w-full max-w-4 text-emerald-800" strokeWidth={3.5} />
);

const isValueFilled = (p, value) =>
  p.type === 'checkbox'
    ? Boolean(value)
    : p.type === 'digits'
      ? digitsComplete(p, value)
      : value !== undefined && value !== null && String(value).trim() !== '';

// Color states: optional = blue, required = amber/orange, filled = green. Fields
// render as a flat colored box (no outline); the outline only appears when the
// field is selected/focused. `required` is the EFFECTIVE required state (incl.
// conditional requiredIf), so a field turns amber the moment it becomes required.
const toneFill = (required, filled) => {
  if (filled) return 'bg-emerald-200/70';
  if (required) return 'bg-amber-200/70';
  return 'bg-blue-200/60';
};
const toneFocusBorder = (required, filled) => {
  if (filled) return 'focus:border-emerald-500 hover:border-emerald-400/60';
  if (required) return 'focus:border-amber-500 hover:border-amber-400/60';
  return 'focus:border-blue-500 hover:border-blue-400/60';
};
// Ring-based focus outline for long_text: a ring doesn't consume layout height,
// so the textarea's content area stays the full box and its line capacity
// matches the builder's count exactly.
const toneFocusRing = (required, filled) => {
  if (filled) return 'focus:ring-emerald-500';
  if (required) return 'focus:ring-amber-500';
  return 'focus:ring-blue-500';
};

const SignField = ({
  p,
  value,
  onChange,
  onOpenSignature,
  onCheckable,
  disabled = false,
  required = false,
  scale = 1.5,
  registerInput,
  onAutoAdvance,
  // Phones (Nic 2026-10-15): the page's boxes are too small to type in, so
  // a tap opens the field in the guide card instead; `active` rings the
  // field the card is on.
  compact = false,
  active = false,
  onOpenGuide,
}) => {
  const style = {
    left: `${p.x * 100}%`,
    top: `${p.y * 100}%`,
    width: `${p.w * 100}%`,
    height: `${p.h * 100}%`,
  };
  const base = cn(
    'absolute flex items-center rounded-sm',
    active && 'z-10 ring-2 ring-amber-500 ring-offset-1',
  );
  // When a conditional rule isn't met the field is greyed + locked. Otherwise its
  // tone reflects the EFFECTIVE required state (amber when required-now).
  const filled = isValueFilled(p, value);
  const fillCls = disabled ? 'bg-neutral-200/60' : toneFill(required, filled);
  const focusCls = disabled ? '' : toneFocusBorder(required, filled);
  const dim = disabled ? 'opacity-60' : '';
  // Font sizes are stored in PDF points; scale to on-screen px so the preview
  // matches the burned PDF (and tracks zoom).
  const baseFontPx = (p.fontSize || 11) * scale;
  const textStyle = { ...style, fontSize: `${baseFontPx}px` };
  const justify =
    p.align === 'center' ? 'justify-center' : p.align === 'right' ? 'justify-end' : 'justify-start';

  // Shrink-to-fit: when enabled, scale the input's font down so whatever is
  // typed always fits the box (mirrors the burn engine).
  const inputRef = useRef(null);
  const [fitFontPx, setFitFontPx] = useState(baseFontPx);
  // Digit boxes: whether its input has the cursor (rings the next box).
  const [digitFocus, setDigitFocus] = useState(false);
  useLayoutEffect(() => {
    if (!p.autoShrink) {
      setFitFontPx(baseFontPx);
      return;
    }
    const el = inputRef.current;
    if (!el) return;
    const avail = el.clientWidth - 6;
    const text = value == null ? '' : String(value);
    let size = baseFontPx;
    while (size > 5 && measureTextWidth(text, size) > avail) size -= 0.5;
    setFitFontPx(size);
  }, [value, p.autoShrink, baseFontPx]);

  // Long text: never allow overflow. The browser wraps the value; if it needs
  // more height than the box, revert to the last value that fit (rejects the
  // overflowing keystroke/paste). Handles wrapping + long words for free.
  // When a keystroke is swallowed the signer gets a brief inline hint —
  // otherwise the rejection is silent and looks like a broken field.
  const taRef = useRef(null);
  const lastFitRef = useRef(value == null ? '' : String(value));
  const [limitHit, setLimitHit] = useState(false);
  const limitTimerRef = useRef(null);
  useEffect(() => () => clearTimeout(limitTimerRef.current), []);
  useLayoutEffect(() => {
    if (p.type !== 'long_text') return;
    const el = taRef.current;
    if (!el) return;
    const cur = value == null ? '' : String(value);
    if (el.scrollHeight > el.clientHeight + 1) {
      // Revert to the last fitting value (guard against a same-value loop).
      if (lastFitRef.current !== cur) {
        onChange(lastFitRef.current);
        setLimitHit(true);
        clearTimeout(limitTimerRef.current);
        limitTimerRef.current = setTimeout(() => setLimitHit(false), 2000);
      }
    } else {
      lastFitRef.current = cur;
    }
    // onChange intentionally omitted: it's recreated per render and only called
    // to revert, which converges in one extra pass.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, p.type]);

  // Prepare-time regions (fee_chart) are already ink on the served PDF and
  // are filtered out server-side; render nothing if one ever slips through.
  if (p.type === 'fee_chart') return null;
  // Digit boxes (2026-10-15): one field shown as its boxes, a character in
  // each. Desktop types into a see-through input over the whole field (the
  // next empty box is ringed); phones tap to open the guide card.
  if (p.type === 'digits') {
    const { boxes } = digitBoxLayout(p);
    const chars = cleanDigitsValue(p, value);
    const masked = p.sensitive && compact;
    const cells = boxes.map((b, i) => (
      <span
        key={i}
        aria-hidden
        className={cn(
          'absolute top-0 flex h-full items-center justify-center rounded-[2px] text-neutral-900',
          fillCls,
          digitFocus && i === Math.min(chars.length, boxes.length - 1) && 'ring-2 ring-sky-500',
        )}
        style={{ left: `${b.left * 100}%`, width: `${b.width * 100}%` }}
      >
        {chars[i] ? (masked ? '•' : chars[i]) : ''}
      </span>
    ));
    if (compact) {
      return (
        <div
          id={`sf-${p.fieldId}`}
          aria-hidden
          className={cn(base, 'pointer-events-none', dim)}
          style={textStyle}
        >
          {cells}
        </div>
      );
    }
    return (
      <div className={cn(base, dim)} style={textStyle}>
        {cells}
        <input
          id={`sf-${p.fieldId}`}
          ref={(el) => {
            inputRef.current = el;
            if (registerInput) registerInput(el);
          }}
          disabled={disabled}
          value={chars}
          onChange={(e) => onChange(cleanDigitsValue(p, e.target.value))}
          onFocus={() => setDigitFocus(true)}
          onBlur={() => setDigitFocus(false)}
          inputMode={p.allowLetters ? 'text' : 'numeric'}
          autoComplete="off"
          aria-label={p.label || p.groupName || 'Number'}
          className="absolute inset-0 h-full w-full cursor-text bg-transparent text-transparent caret-transparent outline-none"
        />
      </div>
    );
  }
  // Phones (Nic 2026-10-15): the page is look-only. Fields show their color
  // and what was typed (sensitive values masked) but take no taps; the
  // wizard card does all the filling. Long text keeps its textarea
  // (read-only, untappable) below, so its fit-the-box check still guards
  // what the card types.
  // "Today's date" (Nic 2026-10-21): shown as text while they read, on every
  // screen size, never an input.
  if (p.type === 'date' && p.autoToday) {
    return (
      <div className={cn(base, justify, 'px-1 text-neutral-700', dim)} style={textStyle}>
        {todayStr()}
      </div>
    );
  }
  if (compact && ['text', 'number', 'date', 'checkbox', 'radio'].includes(p.type)) {
    const shown = value == null || value === false ? '' : value === true ? '' : String(value);
    return (
      <div
        id={`sf-${p.fieldId}`}
        aria-hidden
        className={cn(
          base,
          justify,
          p.type === 'radio' && 'rounded-full',
          'pointer-events-none overflow-hidden whitespace-nowrap px-0.5 text-neutral-900',
          p.type === 'checkbox' && cn(BOX_SQUARE, 'justify-center p-px'),
          fillCls,
          dim,
        )}
        style={textStyle}
      >
        {isCheckableType(p) ? (
          value && p.type === 'radio' ? (
            <span className="block rounded-full bg-emerald-700" style={{ width: '50%', height: '50%' }} />
          ) : value ? (
            <ScaledCheck />
          ) : null
        ) : p.sensitive && shown ? (
          '•'
        ) : (
          shown
        )}
      </div>
    );
  }
  if (p.type === 'static_text') {
    return (
      <div className={cn(base, justify, 'px-1 text-neutral-700', dim)} style={textStyle}>
        {p.staticValue}
      </div>
    );
  }
  if (p.type === 'signature_date') {
    return (
      <div className={cn(base, justify, 'px-1 text-neutral-700', dim)} style={textStyle}>
        {todayStr()}
      </div>
    );
  }
  // Admin-set signature baked into the template — shown to the signer but
  // read-only (they can't edit it).
  if (p.type === 'static_signature') {
    return p.staticValue ? (
      <div className={cn(base, 'justify-center overflow-hidden', dim)} style={style}>
        <img src={p.staticValue} alt="signature" className="max-h-full max-w-full object-contain" />
      </div>
    ) : null;
  }
  if (p.type === 'checkbox') {
    return (
      <button
        type="button"
        id={`sf-${p.fieldId}`}
        // Native <button> gives Enter/Space handling; the role + aria-checked
        // make it read as a checkbox to screen readers.
        role="checkbox"
        aria-checked={Boolean(value)}
        aria-label={p.label || 'Checkbox'}
        disabled={disabled}
        onClick={() => onCheckable(!value)}
        className={cn(
          base,
          BOX_SQUARE,
          'justify-center border-2 border-transparent outline-none transition-colors',
          fillCls,
          focusCls,
          dim,
          disabled && 'cursor-not-allowed',
        )}
        style={style}
      >
        {value ? <ScaledCheck /> : null}
      </button>
    );
  }
  if (p.type === 'radio') {
    return (
      <button
        type="button"
        id={`sf-${p.fieldId}`}
        role="radio"
        aria-checked={Boolean(value)}
        aria-label={p.label || 'Option'}
        disabled={disabled}
        onClick={() => onCheckable(!value)}
        className={cn(
          'absolute flex items-center justify-center rounded-full border-2 border-transparent outline-none transition-colors',
          fillCls,
          focusCls,
          dim,
          disabled && 'cursor-not-allowed',
        )}
        style={style}
      >
        {value ? (
          <span className="block rounded-full bg-emerald-700" style={{ width: '50%', height: '50%' }} />
        ) : null}
      </button>
    );
  }
  if (p.type === 'signature' && compact) {
    return (
      <div
        id={`sf-${p.fieldId}`}
        aria-hidden
        className={cn(
          base,
          'pointer-events-none justify-center overflow-hidden text-[10px] font-medium text-neutral-600',
          disabled ? 'bg-neutral-200/60' : value ? 'bg-transparent' : 'bg-amber-200/70',
          dim,
        )}
        style={style}
      >
        {/* Just the colored box: an icon and "Sign" made it look tappable. */}
        {value ? <img src={value} alt="" className="max-h-full max-w-full object-contain" /> : null}
      </div>
    );
  }
  if (p.type === 'signature') {
    return (
      <button
        type="button"
        id={`sf-${p.fieldId}`}
        disabled={disabled}
        // Phones: the page only selects (Nic 2026-10-15); the pad opens from
        // the card's Tap to sign, which is only reachable at normal zoom, so
        // it never opens huge.
        onClick={onOpenSignature}
        className={cn(
          base,
          'justify-center overflow-hidden border-2 border-transparent text-[10px] font-medium text-neutral-600 outline-none transition-colors',
          disabled
            ? 'cursor-not-allowed bg-neutral-200/60'
            : value
              ? 'bg-transparent focus:border-emerald-500'
              : 'bg-amber-200/70 hover:border-amber-400/60 focus:border-amber-500',
          dim,
        )}
        style={style}
      >
        {value ? (
          <img src={value} alt="signature" className="max-h-full max-w-full object-contain" />
        ) : (
          <span className="flex items-center gap-1">
            <PenLine className="h-3 w-3" /> Sign
          </span>
        )}
      </button>
    );
  }
  if (p.type === 'long_text') {
    return (
      <>
        <textarea
          ref={taRef}
          id={`sf-${p.fieldId}`}
          disabled={disabled}
          readOnly={compact}
          tabIndex={compact ? -1 : undefined}
          value={value || ''}
          onChange={(e) => {
            let v = e.target.value;
            if (p.maxLength && v.length > p.maxLength) v = v.slice(0, p.maxLength);
            onChange(v);
          }}
          maxLength={p.maxLength || undefined}
          aria-label={p.label || 'Field'}
          // No border/vertical padding so the content area == the full box height,
          // so its line capacity matches the builder's count; ring = focus outline.
          className={cn(
            'absolute resize-none overflow-hidden rounded-sm px-1 py-0 text-neutral-900 outline-none transition-colors focus:ring-2',
            compact && 'pointer-events-none',
            active && 'z-10 ring-2 ring-amber-500 ring-offset-1',
            fillCls,
            disabled ? 'cursor-not-allowed' : toneFocusRing(required, filled),
            dim,
          )}
          style={{ ...textStyle, textAlign: p.align || 'left', lineHeight: LINE_HEIGHT_FACTOR }}
        />
        {/* Brief hint when a keystroke/paste was rejected because the box is
            full — positioned just under the field. role="status" announces it. */}
        {limitHit ? (
          <div
            role="status"
            className="absolute z-10 whitespace-nowrap rounded bg-status-error-bg px-1.5 py-0.5 text-[10px] font-medium text-status-error-fg shadow-sm"
            style={{ left: `${p.x * 100}%`, top: `calc(${(p.y + p.h) * 100}% + 2px)` }}
          >
            Character limit reached
          </div>
        ) : null}
      </>
    );
  }
  return (
    <input
      id={`sf-${p.fieldId}`}
      ref={(el) => {
        inputRef.current = el;
        if (registerInput) registerInput(el);
      }}
      disabled={disabled}
      // Number fields render as plain text (no spinner arrows) but only accept
      // numeric input; inputMode brings up the numeric keypad on mobile.
      type={p.type === 'date' ? 'date' : 'text'}
      inputMode={p.type === 'number' ? 'decimal' : undefined}
      value={value || ''}
      onChange={(e) => {
        let v = e.target.value;
        // Numbers only: keep digits + a single decimal point.
        if (p.type === 'number') {
          v = v.replace(/[^\d.]/g, '');
          const dot = v.indexOf('.');
          if (dot !== -1) v = v.slice(0, dot + 1) + v.slice(dot + 1).replace(/\./g, '');
        }
        // Enforce the character cap (the maxLength attribute is ignored on
        // number inputs, so clamp here too).
        if (p.maxLength && v.length > p.maxLength) v = v.slice(0, p.maxLength);
        // Limit-to-box: drop trailing characters that won't fit the box width.
        if (p.limitToBox && inputRef.current) {
          const avail = inputRef.current.clientWidth - 6;
          while (v.length && measureTextWidth(v, baseFontPx) > avail) v = v.slice(0, -1);
        }
        // Auto-advance: when this field just FILLED to its max-character cap, jump
        // focus to the next field (next in its group by order; else next on the doc).
        const justFilled =
          p.autoAdvance &&
          p.maxLength &&
          (value || '').length < p.maxLength &&
          v.length >= p.maxLength;
        onChange(v);
        if (justFilled && onAutoAdvance) onAutoAdvance();
      }}
      maxLength={p.maxLength || undefined}
      aria-label={p.label || 'Field'}
      className={cn(
        base,
        // Ring (not border) for focus: a 2px border is fixed-size and doesn't
        // scale with zoom, so at 100% it ate the box height and clipped the text
        // in short boxes (e.g. SSN digit cells). A ring doesn't consume layout
        // height, so the content area stays the full box. Same trick as long_text.
        'px-1 text-neutral-900 outline-none transition-colors',
        !disabled && 'focus:ring-2',
        fillCls,
        disabled ? '' : toneFocusRing(required, filled),
        dim,
        disabled && 'cursor-not-allowed',
      )}
      style={{
        ...textStyle,
        fontSize: `${p.autoShrink ? fitFontPx : baseFontPx}px`,
        textAlign: p.align || 'left',
      }}
    />
  );
};

// One line, always (Nic 2026-10-05: on a phone each pill wrapped into a tall
// two-line block). Compact on phones, the old size from sm up.
const StatBanner = ({ label, done, total, complete }) => (
  <div
    className={cn(
      'inline-flex items-center gap-1 whitespace-nowrap rounded-full px-2.5 py-0.5 text-xs font-medium transition-colors sm:gap-1.5 sm:px-3 sm:py-1 sm:text-sm',
      complete ? 'bg-emerald-100 text-emerald-800' : 'bg-amber-100 text-amber-800',
    )}
  >
    {complete ? <Check className="h-3.5 w-3.5 sm:h-4 sm:w-4" /> : null}
    {label}
    <span className="font-semibold tabular-nums">
      {done}/{total}
    </span>
  </div>
);

const SignDocument = ({ previewMode = false, previewDoc = null, onClosePreview }) => {
  const { token } = useParams();
  const { data, isLoading, isError, error, refetch } = useFetchSignSession(
    previewMode ? null : token,
  );
  const submit = useSubmitSessionDocument();

  const [valuesByEnv, setValuesByEnv] = useState({});
  const [consent, setConsent] = useState(false);
  const [sigField, setSigField] = useState(null);
  const [submitError, setSubmitError] = useState(null);
  const [ready, setReady] = useState(false);
  const [zoom, setZoom] = useState(1);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [completedSet, setCompletedSet] = useState(() => new Set());
  const [sessionDone, setSessionDone] = useState(false);
  // fieldId -> input element, for auto-advance focus jumps between grouped fields.
  const inputsRef = useRef({});
  // fieldId -> { mode, typedName } so reopening a signature returns to how it was
  // captured (e.g. a typed name reopens the Type view, pre-filled), since the saved
  // value is just a PNG and can't reveal whether it was drawn or typed.
  const sigMetaRef = useRef({});
  // Set when the final signature puts a referral partner applicant into the
  // review queue — swaps the completion copy for an "under review" notice (the
  // personal link + QR arrive by email once a reviewer approves).
  const [underReview, setUnderReview] = useState(false);
  const [signedPurpose, setSignedPurpose] = useState(null);
  const [pdfDoc, setPdfDoc] = useState(null);
  // PDF load/render failure (expired presigned URL, S3 CORS, network). Renders
  // a visible error + Reload instead of the old silent blank-page fallback — a
  // signer must never fill/submit legally binding fields over an unrendered
  // document. Reload refetches the session (fresh presigned URLs) + retries.
  const [pdfError, setPdfError] = useState(null);
  const [loadAttempt, setLoadAttempt] = useState(0);

  // pageNum -> { url, width, height, scale } for the current document's
  // rendered pages (width/height in CSS px at render time).
  const [pageImages, setPageImages] = useState({});
  // The pdf the images were last drawn for: a zoom on the same pdf waits a
  // beat (clicks coalesce); a new document draws at once.
  const drawnForRef = useRef(null);
  useEffect(
    () => () => {
      // Free the page images when the signer closes.
      setPageImages((prev) => {
        Object.values(prev).forEach((img) => URL.revokeObjectURL(img.url));
        return {};
      });
    },
    [],
  );
  // The guide card's step (null = closed). Phones fill through it.
  const [guideIdx, setGuideIdx] = useState(null);
  const initedRef = useRef(false);
  // Highest document index the signer has reached — the step dots can jump to
  // any doc at or below this, but never forward past it.
  const [maxVisitedIndex, setMaxVisitedIndex] = useState(0);
  // Re-entry guard for Finish: `submit.isLoading` alone races a fast second
  // click during the micro-gap between the sequential per-document submits.
  const submittingRef = useRef(false);

  // Responsive base render width: min(RENDER_WIDTH, container width). The page
  // wrapper centers with no horizontal scroll, so a fixed 720 left the left
  // half of the document unreachable on phones. Callback-ref state (not a
  // plain ref) so the observer attaches when the surface mounts AFTER the
  // loading/error early returns below.
  const [surfaceEl, setSurfaceEl] = useState(null);
  const [baseWidth, setBaseWidth] = useState(RENDER_WIDTH);
  // Viewport width decides HOW a zoomed page displays: while it still fits the
  // viewport the card simply grows (centered — the natural "zoom makes it
  // bigger" feel); only when it exceeds the viewport does the row become a
  // left-anchored horizontal scroller (the phone-reachability case).
  const [viewportW, setViewportW] = useState(() => window.innerWidth);
  const [touchOnly] = useState(isTouchOnly);

  // The signer always lets the page zoom (Nic 2026-10-24). index.html sets
  // maximum-scale=1 on iPhones (it stops the auto-zoom into small inputs);
  // Safari ignores it for pinch, but the Gmail, Outlook and Facebook in-app
  // browsers obey it, so a client opening the link there couldn't zoom the
  // contract at all. The signer's typing happens in the 16px card, so it
  // never auto-zooms. resizes-content keeps the bottom card above the
  // Android keyboard. Restored on the way out.
  useEffect(() => {
    if (previewMode) return undefined;
    const meta = document.querySelector('meta[name="viewport"]');
    if (!meta) return undefined;
    const before = meta.getAttribute('content');
    meta.setAttribute('content', 'width=device-width, initial-scale=1, interactive-widget=resizes-content');
    return () => meta.setAttribute('content', before);
  }, [previewMode]);

  useLayoutEffect(() => {
    if (!surfaceEl) return undefined;
    const measure = () => {
      const w = surfaceEl.clientWidth;
      if (w > 0) setBaseWidth(Math.min(RENDER_WIDTH, Math.max(MIN_RENDER_WIDTH, Math.floor(w))));
      setViewportW(window.innerWidth);
    };
    measure();
    // Debounced re-measure: re-rendering PDF pages is expensive, so wait for
    // the resize/rotation to settle before kicking off a re-render.
    let timer = null;
    const observer = new ResizeObserver(() => {
      clearTimeout(timer);
      timer = setTimeout(measure, 150);
    });
    observer.observe(surfaceEl);
    window.addEventListener('resize', measure);
    return () => {
      clearTimeout(timer);
      observer.disconnect();
      window.removeEventListener('resize', measure);
    };
  }, [surfaceEl]);
  const zoomFitsViewport = baseWidth * zoom <= viewportW - 24;
  // The width pages are SHOWN at. Zoom applies instantly by scaling the
  // current images; sharper ones replace them a moment later (Nic
  // 2026-10-15: re-drawing every page first made each zoom click lag).
  const displayWidth = baseWidth * zoom;
  // CSS px per PDF point at the shown size: text overlays (font sizes are in
  // PDF points) match the burned PDF and follow zoom without waiting for the
  // re-draw. 1.5 is a sane default for US Letter before the first draw.
  const page1 = pageImages[1];
  const pageScale = page1
    ? displayWidth / (page1.width / page1.scale)
    : 1.5;

  const documents = useMemo(() => {
    if (previewMode) {
      return previewDoc
        ? [
            {
              envelopeId: 'preview',
              name: previewDoc.name,
              description: previewDoc.description,
              pageCount: previewDoc.pageCount || 1,
              pdfUrl: previewDoc.pdfUrl,
              fieldPlacements: previewDoc.fieldPlacements || [],
              completed: false,
            },
          ]
        : [];
    }
    return data?.documents || [];
  }, [previewMode, previewDoc, data]);

  const sessionCompleted = !previewMode && data?.completed;

  // First load: seed completed set + jump to the first unsigned document.
  useEffect(() => {
    if (initedRef.current || !documents.length) return;
    const set = new Set(documents.filter((d) => d.completed).map((d) => d.envelopeId));
    setCompletedSet(set);
    const firstIncomplete = documents.findIndex((d) => !set.has(d.envelopeId));
    const startIdx = firstIncomplete >= 0 ? firstIncomplete : 0;
    setCurrentIndex(startIdx);
    setMaxVisitedIndex(startIdx);
    initedRef.current = true;
  }, [documents]);

  const current = documents[currentIndex] || null;
  const placements = useMemo(() => current?.fieldPlacements || [], [current]);
  const envId = current?.envelopeId;
  const values = (envId && valuesByEnv[envId]) || EMPTY;

  // When the active document changes, drop "ready" BEFORE paint so we never flash
  // the next document's fields over the previous document's still-rendered PDF.
  useLayoutEffect(() => {
    setReady(false);
    setGuideIdx(null);
  }, [envId]);

  // Load the current document's PDF whenever its URL changes. Kept separate from
  // rendering (below) so we never paint fields over an unrendered page and so a
  // zoom change doesn't re-fetch. State-driven (not ref-driven) so React 18
  // StrictMode's double-invoke can't leave us "ready" with no PDF painted.
  useEffect(() => {
    if (!current?.pdfUrl) {
      // No document to load — don't spin forever (e.g. an unexpected empty doc).
      setReady(true);
      return undefined;
    }
    let cancelled = false;
    setReady(false);
    setPdfDoc(null);
    setPdfError(null);
    (async () => {
      try {
        const { pdf } = await loadPdfDocument(current.pdfUrl);
        if (!cancelled) setPdfDoc(pdf);
      } catch (e) {
        // Surface the failure (pdfRender throws a descriptive message for
        // expired links) — fields stay hidden and submit stays blocked.
        if (!cancelled) setPdfError(e?.message || 'Could not load the document.');
      }
    })();
    return () => {
      cancelled = true;
    };
    // loadAttempt: manual Reload retries the fetch even for the same URL.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [current?.pdfUrl, loadAttempt]);

  // Reload after a failed PDF load: refetch the session first (presigned URLs
  // expire after ~1h — a stale tab needs FRESH urls, not a retry of the dead
  // one), then retry the load. Entered values are keyed by envelopeId and
  // survive the refetch.
  const reloadPdf = () => {
    if (!previewMode) refetch();
    setLoadAttempt((a) => a + 1);
  };

  // Render (or re-render on zoom) once the PDF is loaded. The spinner stays up
  // until the pages are actually painted, then the field overlay appears.
  // Pages are images drawn at the screen's density, doubled on phones so a
  // pinch-zoom stays sharp (Nic 2026-10-15); old images are freed on re-render.
  // Pages are drawn one by one and shown as each lands (Nic 2026-10-24): the
  // first page appears right away instead of after the whole packet, and on
  // touch devices the packet shares one memory budget. `drawnPages` counts
  // the pages drawn for the current document: Continue / Finish wait for all
  // of them, so nobody signs before every page has shown.
  const [drawnPages, setDrawnPages] = useState(0);
  useEffect(() => {
    if (!pdfDoc) return undefined;
    let cancelled = false;
    const made = [];
    const sameDoc = drawnForRef.current === pdfDoc;
    const delay = sameDoc ? 300 : 0;
    if (!sameDoc) {
      // A new document: nothing from the last one may show under its fields.
      setDrawnPages(0);
      setPageImages((prev) => {
        Object.values(prev).forEach((img) => URL.revokeObjectURL(img.url));
        return {};
      });
    }
    const timer = setTimeout(async () => {
      try {
        // Page count comes from the LOADED pdf — never from `current` — so this
        // effect can't fire with a stale pdfDoc when switching documents.
        const numPages = pdfDoc.numPages || 1;
        const small = touchOnly || window.innerWidth <= NARROW_MAX;
        const oversample = small ? 2 : 1;
        const maxPixels = small ? TOUCH_PACKET_PIXELS / numPages : undefined;
        for (let i = 1; i <= numPages; i += 1) {
          if (cancelled) break;
          // eslint-disable-next-line no-await-in-loop
          const res = await renderPageToImageUrl(pdfDoc, i, baseWidth * zoom, { oversample, maxPixels });
          // Superseded while drawing (QA 2026-10-15): the cleanup already
          // ran, so free this one here or it leaks.
          if (cancelled) {
            URL.revokeObjectURL(res.url);
            break;
          }
          made.push(res.url);
          setPageImages((prev) => {
            const old = prev[i];
            if (old && old.url !== res.url) URL.revokeObjectURL(old.url);
            return { ...prev, [i]: res };
          });
          // Redraws count too (QA 2026-10-26): a zoom, rotate or resize before
          // the last page lands restarts as a same-doc redraw, and only
          // counting first draws left Continue / Finish disabled for good.
          setDrawnPages((n) => Math.max(n, i));
          if (i === 1) {
            drawnForRef.current = pdfDoc;
            setReady(true);
          }
        }
      } catch (e) {
        if (!cancelled) setPdfError('Could not display the document. Please reload.');
      }
    }, delay);
    return () => {
      cancelled = true;
      clearTimeout(timer);
      // A render that was superseded frees whatever it drew.
      setPageImages((prev) => {
        const kept = new Set(Object.values(prev).map((img) => img.url));
        made.filter((u) => !kept.has(u)).forEach((u) => URL.revokeObjectURL(u));
        return prev;
      });
    };
    // Intentionally NOT keyed on `current` — only the loaded pdf + zoom + base
    // width. Keying on current?.pageCount would re-run this with a stale pdfDoc
    // mid-switch and paint the previous document onto the new one.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pdfDoc, zoom, baseWidth]);
  const allPagesDrawn = !current?.pdfUrl || (!!pdfDoc && drawnPages >= (pdfDoc.numPages || 1));

  const setValue = (fieldId, v) =>
    setValuesByEnv((prev) => ({ ...prev, [envId]: { ...(prev[envId] || {}), [fieldId]: v } }));

  // Toggle a checkbox/radio. In a single-choice group, turning one on clears the
  // group's other checkable members (works for checkboxes and radios alike).
  const onCheckableChange = (p, nextVal) =>
    setValuesByEnv((prev) => {
      const cur = prev[envId] || {};
      const next = { ...cur };
      if (nextVal && p.group && p.groupSingle) {
        placements.forEach((q) => {
          if ((q.type === 'checkbox' || q.type === 'radio') && q.group === p.group && q.groupSingle) {
            next[q.fieldId] = false;
          }
        });
      }
      next[p.fieldId] = nextVal;
      return { ...prev, [envId]: next };
    });

  // Required items: a normal required field counts as one; a single-choice group
  // whose members include a required one counts as a single item, satisfied by
  // any member being selected.
  const requiredItems = useMemo(() => {
    const items = [];
    const seen = new Set();
    const isCheckable = (q) => q.type === 'checkbox' || q.type === 'radio';
    for (const p of placements) {
      if (p.type === 'signature' || isAutoField(p)) continue;
      if (!fieldEnabled(p, values)) continue; // disabled (conditional) fields aren't required
      // Half-filled digit boxes must be finished or cleared, even inside a
      // "Require one of these" group (QA 2026-10-15: the page let Finish on
      // while the server refused "SSN (fill every box)"). Same order as the
      // server's firstMissingRequired.
      const halfDigits =
        p.type === 'digits' &&
        cleanDigitsValue(p, values[p.fieldId]) !== '' &&
        !digitsComplete(p, values[p.fieldId]);
      if (halfDigits) items.push({ type: 'field', p });
      if (isCheckable(p) && p.group && (p.groupSingle || p.groupRequired)) {
        // Checkable and typed members keep separate group keys (QA
        // 2026-10-15): a group with both rules needs both met.
        if (seen.has(`c:${p.group}`)) continue;
        seen.add(`c:${p.group}`);
        const members = placements.filter(
          (q) => isCheckable(q) && q.group === p.group && fieldEnabled(q, values),
        );
        // The group is one required item (satisfied by any member) when it
        // "requires a selection", or it's single-choice with a required member.
        const requireOne = members.some((m) => m.groupRequired);
        const singleRequired =
          members.some((m) => m.groupSingle) && members.some((m) => fieldRequired(m, values));
        if (requireOne || singleRequired) items.push({ type: 'group', members });
        continue;
      }
      // "Require one of these" (typed members, 2026-10-15): one item,
      // satisfied by any member filled in full (an SSN or an EIN).
      if (!isCheckable(p) && p.group && p.groupRequired) {
        // A member also marked Required keeps that, like the server.
        if (fieldRequired(p, values) && !halfDigits) items.push({ type: 'field', p });
        if (seen.has(`t:${p.group}`)) continue;
        seen.add(`t:${p.group}`);
        items.push({
          type: 'group',
          members: placements.filter(
            (q) => !isCheckable(q) && q.group === p.group && q.groupRequired && fieldEnabled(q, values),
          ),
        });
        continue;
      }
      if (fieldRequired(p, values) && !halfDigits) items.push({ type: 'field', p });
    }
    return items;
  }, [placements, values]);
  const signatureFields = useMemo(
    () => placements.filter((p) => p.type === 'signature' && fieldEnabled(p, values)),
    [placements, values],
  );
  const isFilled = useCallback((p) => isValueFilled(p, values[p.fieldId]), [values]);

  // Whether any checkable member of a group is selected.
  const groupHasSelection = useCallback(
    (gid) =>
      placements.some(
        (q) =>
          q.group === gid &&
          (q.type === 'checkbox' || q.type === 'radio') &&
          values[q.fieldId],
      ),
    [placements, values],
  );
  // Effective required state that drives the field tone. A checkable member of a
  // "require a selection" group reads as required (amber) until ANY member is
  // selected — then the group is satisfied and the rest relax to optional (blue),
  // the chosen one filled (green). Everything else uses its normal required state.
  const effectiveRequired = useCallback(
    (p) => {
      if ((p.type === 'checkbox' || p.type === 'radio') && p.group && p.groupRequired) {
        return !groupHasSelection(p.group);
      }
      // Half-filled digit boxes read as required until finished or cleared.
      if (
        p.type === 'digits' &&
        cleanDigitsValue(p, values[p.fieldId]) !== '' &&
        !digitsComplete(p, values[p.fieldId])
      ) {
        return true;
      }
      // "Require one of these": amber until any member is filled in full.
      if (p.group && p.groupRequired) {
        return (
          fieldRequired(p, values) ||
          !placements.some(
            (q) => q.group === p.group && q.groupRequired && isValueFilled(q, values[q.fieldId]),
          )
        );
      }
      return fieldRequired(p, values);
    },
    [groupHasSelection, values, placements],
  );

  const isItemDone = (item) =>
    item.type === 'group'
      ? item.members.some((m) =>
          m.type === 'checkbox' || m.type === 'radio' ? Boolean(values[m.fieldId]) : isFilled(m),
        )
      : isFilled(item.p);
  const requiredDone = requiredItems.filter(isItemDone).length;
  const sigDone = signatureFields.filter((p) => values[p.fieldId]).length;
  const allRequiredDone = requiredDone === requiredItems.length;
  const allSigDone = sigDone === signatureFields.length;
  const currentDocDone = allRequiredDone && allSigDone;

  const remaining = documents.filter((d) => !completedSet.has(d.envelopeId));
  // "Final step" = the current doc is the last incomplete one. When the signer
  // navigates BACK to an already-filled doc, this goes false so the consent +
  // Finish button only ever appear on the final document.
  const isLastRemaining = remaining.length <= 1 && !completedSet.has(envId);
  // Continuing to the next document only needs the current doc filled. The e-sign
  // consent + the actual submission happen once, on the final document. docReady
  // blocks progress while the current document's pages are unrendered/failed —
  // nobody signs over a blank card.
  const docReady = ready && allPagesDrawn && !pdfError;
  const canContinue = currentDocDone && docReady && !submit.isLoading;
  const canFinish = currentDocDone && docReady && consent && !submit.isLoading;

  // ---- Guided filling (Nic 2026-10-15) ----
  // Phones fill one field at a time in the card; desktop keeps typing on the
  // page and gets a "Next field" jump.
  const narrow = touchOnly || viewportW <= NARROW_MAX;

  const steps = useMemo(() => buildGuideSteps(placements, values), [placements, values]);
  const guideStep = guideIdx != null ? steps[guideIdx] || null : null;
  // Stay on the same field when a choice adds or removes steps before it
  // (QA 2026-10-15: a conditional field earlier in reading order shifted the
  // index and the card jumped to a different field).
  // Only a change in the steps themselves re-finds the field; Back / Next
  // move the index on purpose.
  const guideKeyRef = useRef(null);
  const guideStepsRef = useRef(steps);
  useEffect(() => {
    const stepsChanged = guideStepsRef.current !== steps;
    guideStepsRef.current = steps;
    if (guideIdx == null) {
      guideKeyRef.current = null;
      return;
    }
    if (stepsChanged && guideKeyRef.current) {
      const at = steps.findIndex((st) => st.key === guideKeyRef.current);
      if (at >= 0 && at !== guideIdx) {
        setGuideIdx(at);
        return;
      }
    }
    guideKeyRef.current = steps[guideIdx]?.key || null;
  }, [steps, guideIdx]);
  const activeIds = useMemo(
    () => new Set(guideStep ? guideStep.fields.map((f) => f.fieldId) : []),
    [guideStep],
  );
  const firstOpen = nextOpenStep(steps, values);
  // Bring a field up into the top part of the screen (the card covers the
  // bottom on a phone).
  // Scrolls whatever holds the page: the window on the signing page, the
  // preview overlay in the builder's Preview (Nic 2026-10-15: there the
  // window never moved, so the arrow walked off screen).
  const scrollToField = (fieldId) => {
    const el = fieldId && document.getElementById(`sf-${fieldId}`);
    if (!el) return;
    let box = el.parentElement;
    while (box && box !== document.body) {
      const oy = window.getComputedStyle(box).overflowY;
      if ((oy === 'auto' || oy === 'scroll') && box.scrollHeight > box.clientHeight) break;
      box = box.parentElement;
    }
    const rect = el.getBoundingClientRect();
    const share = narrow ? 0.22 : 0.35;
    if (box && box !== document.body) {
      const outer = box.getBoundingClientRect();
      box.scrollTo({
        top: box.scrollTop + rect.top - outer.top - box.clientHeight * share,
        behavior: 'smooth',
      });
      return;
    }
    window.scrollTo({
      top: window.scrollY + rect.top - window.innerHeight * share,
      behavior: 'smooth',
    });
  };
  // Set when the signer clicks into a field themselves: the tag moves there
  // without scrolling the page out from under them.
  const skipScrollRef = useRef(false);
  useEffect(() => {
    if (skipScrollRef.current) {
      skipScrollRef.current = false;
      return;
    }
    if (guideStep) scrollToField(guideStep.anchorId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [guideStep?.key]);
  // Desktop: whichever field the signer is in, the margin tag follows (not
  // once the document is all filled in).
  const onPageFocus = (e) => {
    if (narrow || currentDocDone) return;
    const id = e.target?.id || '';
    if (!id.startsWith('sf-')) return;
    const fieldId = id.slice(3);
    const i = steps.findIndex((st) => st.fields.some((f) => f.fieldId === fieldId));
    if (i >= 0 && i !== guideIdx) {
      skipScrollRef.current = true;
      setGuideIdx(i);
    }
  };
  // A finished field doesn't keep the tag (Nic 2026-10-24: it stayed on a
  // signed box). After a signature, like DocuSign, the tag moves to the next
  // open field; once the whole document is filled it goes away, outline and
  // all. Phones walk on through the card.
  const advanceAfterSignRef = useRef(false);
  useEffect(() => {
    if (!advanceAfterSignRef.current) return;
    advanceAfterSignRef.current = false;
    const i = nextOpenStep(steps, values, guideIdx ?? -1);
    setGuideIdx(i == null ? null : i);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [values]);
  useEffect(() => {
    if (!narrow && currentDocDone) setGuideIdx(null);
  }, [narrow, currentDocDone]);

  // The margin tag: go to its field, cursor in it (or the signature pad open).
  const goToStep = (st) => {
    if (!st) return;
    scrollToField(st.anchorId);
    if (st.kind === 'signature') {
      setSigField(st.anchorId);
      return;
    }
    const firstEmpty =
      st.fields.find((f) => !values[f.fieldId] || String(values[f.fieldId]).trim() === '') ||
      st.fields[0];
    const el =
      inputsRef.current[firstEmpty.fieldId] || document.getElementById(`sf-${firstEmpty.fieldId}`);
    if (el) setTimeout(() => el.focus({ preventScroll: true }), 250);
  };
  const openGuideAtField = (fieldId) => {
    const i = steps.findIndex((st) => st.fields.some((f) => f.fieldId === fieldId));
    if (i >= 0) setGuideIdx(i);
  };
  const guideNext = () => {
    if (guideIdx == null) return;
    if (guideIdx >= steps.length - 1) {
      setGuideIdx(null);
      return;
    }
    setGuideIdx(guideIdx + 1);
  };
  // "Next field": phones open the card on the next open field; desktop
  // scrolls to it and puts the cursor in it (or opens the signature pad).
  const goNextField = () => {
    // Start goes to the very first field (Nic 2026-10-15); after that it
    // walks to the next one still open.
    const starting = guideIdx == null && requiredDone + sigDone === 0;
    const i = starting && steps.length ? 0 : nextOpenStep(steps, values, guideIdx ?? -1);
    if (i == null) return;
    if (narrow) {
      setGuideIdx(i);
      return;
    }
    const st = steps[i];
    setGuideIdx(i);
    scrollToField(st.anchorId);
    // A signature waits for the signer to click it (the arrow points at it).
    if (st.kind === 'signature') return;
    const firstEmpty =
      st.fields.find((f) => !values[f.fieldId] || String(values[f.fieldId]).trim() === '') ||
      st.fields[0];
    const el = inputsRef.current[firstEmpty.fieldId];
    if (el) setTimeout(() => el.focus({ preventScroll: true }), 250);
  };
  // A limited box only takes what fits at the page's size (the page's rule).
  const fitsBox = (p, v) => {
    if (!p.limitToBox) return true;
    const avail = p.w * baseWidth * zoom - 6;
    return measureTextWidth(String(v || ''), (p.fontSize || 11) * pageScale) <= avail;
  };

  // Whether the signer has typed/checked/signed anything not yet submitted —
  // drives the tab-close warning below.
  const hasEnteredValues = useMemo(
    () =>
      Object.values(valuesByEnv).some((env) =>
        Object.values(env || {}).some(
          (v) => v !== undefined && v !== null && v !== false && String(v).trim() !== '',
        ),
      ),
    [valuesByEnv],
  );

  // Saved progress (Nic 2026-10-24, lib in ./signDraft): restored once when
  // the packet opens, saved a moment after each change and right away when
  // the app goes to the background (where iPhones may drop the tab), cleared
  // on Finish. Signatures and sensitive fields are never kept.
  const sessionId = data?.sessionId || null;
  const restoredRef = useRef(false);
  useEffect(() => {
    if (previewMode || restoredRef.current || !sessionId || !documents.length || sessionCompleted) return;
    restoredRef.current = true;
    const draft = loadSignDraft(sessionId, documents);
    if (!draft) return;
    setValuesByEnv((prev) => {
      const next = { ...prev };
      Object.entries(draft).forEach(([env, vals]) => {
        next[env] = { ...vals, ...(prev[env] || {}) };
      });
      return next;
    });
    toast.info('We kept what you entered earlier.');
  }, [previewMode, sessionId, documents, sessionCompleted]);
  const draftRef = useRef(null);
  draftRef.current = { sessionId, valuesByEnv, documents, done: sessionDone || !!sessionCompleted };
  useEffect(() => {
    if (previewMode || !sessionId || sessionDone || !restoredRef.current) return undefined;
    const t = setTimeout(() => saveSignDraft(sessionId, valuesByEnv, documents), 400);
    return () => clearTimeout(t);
  }, [previewMode, sessionId, sessionDone, valuesByEnv, documents]);
  useEffect(() => {
    if (previewMode) return undefined;
    const flush = () => {
      const d = draftRef.current;
      if (d?.sessionId && !d.done && restoredRef.current) saveSignDraft(d.sessionId, d.valuesByEnv, d.documents);
    };
    const onHide = () => {
      if (document.visibilityState === 'hidden') flush();
    };
    document.addEventListener('visibilitychange', onHide);
    window.addEventListener('pagehide', flush);
    return () => {
      document.removeEventListener('visibilitychange', onHide);
      window.removeEventListener('pagehide', flush);
    };
  }, [previewMode]);
  useEffect(() => {
    if (sessionDone || sessionCompleted) clearSignDraft(sessionId);
  }, [sessionDone, sessionCompleted, sessionId]);

  // Nothing persists until "Finish & Submit" (deferred multi-doc submit), so
  // closing the tab would silently lose everything. Modern browsers show their
  // own generic message; setting returnValue is what actually arms the prompt.
  useEffect(() => {
    if (previewMode || sessionDone || !hasEnteredValues) return undefined;
    const warn = (e) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [previewMode, sessionDone, hasEnteredValues]);

  // Jump to an already-visited document (step dots + Back button). Purely UI —
  // inputs are preserved per-envelope in valuesByEnv, nothing is persisted.
  const goToDocument = (idx) => {
    if (idx < 0 || idx > maxVisitedIndex || idx === currentIndex) return;
    setCurrentIndex(idx);
  };

  // Advance to the next unfinished document WITHOUT persisting anything — nothing
  // is saved until "Finish & Submit", so backing out mid-packet leaves no record.
  const handleContinue = () => {
    if (!currentDocDone) return;
    const newSet = new Set(completedSet);
    newSet.add(envId);
    setCompletedSet(newSet);
    const nextIdx = documents.findIndex((d) => !newSet.has(d.envelopeId));
    if (nextIdx >= 0) {
      setCurrentIndex(nextIdx);
      setMaxVisitedIndex((m) => Math.max(m, nextIdx));
    }
  };

  // Submit the whole packet at once: POST every not-yet-saved document in order
  // (the final one completes the session; referral applicants land in the
  // review queue — the response's underReview flag drives the notice below).
  const handleFinish = async () => {
    if (previewMode) {
      toast.info('Preview mode — nothing was submitted.');
      return;
    }
    if (submittingRef.current) return;
    submittingRef.current = true;
    setSubmitError(null);
    const toSubmit = documents.filter((d) => !d.completed);
    try {
      let lastRes;
      for (const d of toSubmit) {
        try {
          // eslint-disable-next-line no-await-in-loop
          lastRes = await submit.mutateAsync({
            token,
            envelopeId: d.envelopeId,
            fieldValues: valuesByEnv[d.envelopeId] || {},
            consent,
          });
        } catch (err) {
          // A 409 means this envelope was already signed — e.g. a retry after a
          // partial-failure submit where an earlier doc went through. It's done
          // server-side, so skip it and continue the packet; rethrow real errors.
          if (err?.response?.status !== 409) throw err;
        }
      }
      // Done only when the server says the packet closed (QA 2026-10-26): a
      // void or resend mid-submit used to show "All done" with nothing
      // recorded. When the last answer didn't close it (every doc came back
      // 409 on a retry, or one didn't land), ask the server where the packet
      // stands; that also brings back the purpose, so a client still gets
      // "Go to your dashboard" after a retry.
      let purpose = lastRes?.sessionCompleted ? lastRes.sessionPurpose : null;
      let closed = Boolean(lastRes?.sessionCompleted);
      if (!closed) {
        let check = null;
        try {
          check = (await axios.get(`/public/sign/${token}`, { timeout: SIGN_LOAD_TIMEOUT_MS })).data;
        } catch (err) {
          // No answer at all: the usual "couldn't reach our server" retry.
          if (!err?.response) throw err;
        }
        closed = Boolean(check?.completed);
        purpose = check?.sessionPurpose || null;
        if (!closed) {
          setSubmitError(
            check
              ? 'Some of your documents did not go through. Please tap Finish & Submit again.'
              : REPLACED_MSG,
          );
          return;
        }
      }
      if (lastRes?.underReview) setUnderReview(true);
      if (purpose) setSignedPurpose(purpose);
      setSessionDone(true);
    } catch (e) {
      setSubmitError(
        e?.response?.data?.msg ||
          (!e?.response
            ? "We couldn't reach our server. Check your connection and tap Finish & Submit again. Nothing you entered was lost."
            : 'Could not submit. Please try again.'),
      );
    } finally {
      submittingRef.current = false;
    }
  };

  // ---- states (skipped in preview) ----
  if (!previewMode && isLoading) {
    return (
      <PublicLayout>
        <div className="flex items-center justify-center py-24 text-muted-foreground">
          <Loader2 className="h-6 w-6 animate-spin" />
        </div>
      </PublicLayout>
    );
  }

  if (!previewMode && isError) {
    // Say what actually happened (Nic 2026-10-24): a weak signal or a busy
    // moment isn't a dead link, and gets a Retry instead of a dead end.
    const status = error?.response?.status;
    const temporary = isRetryableSignError(error) || status === 429;
    return (
      <PublicLayout>
        <div className="mx-auto max-w-md px-4 py-20 text-center">
          {temporary ? (
            <>
              <h1 className="text-xl font-bold text-foreground">
                {status === 429 ? 'Too many tries at once' : "We couldn't reach our server"}
              </h1>
              <p className="mt-2 text-muted-foreground">
                {status === 429
                  ? 'Please wait a minute, then try again.'
                  : 'Check your internet connection, then try again. Anything you already entered is kept on this device.'}
              </p>
              <button
                type="button"
                onClick={() => refetch()}
                className="mt-4 inline-flex items-center justify-center rounded-md bg-primary px-5 py-2 text-sm font-semibold text-primary-foreground hover:opacity-90"
              >
                Try again
              </button>
            </>
          ) : (
            <>
              <h1 className="text-xl font-bold text-foreground">Link unavailable</h1>
              <p className="mt-2 text-muted-foreground">
                {error?.response?.data?.msg || 'This signing link is invalid or has expired.'}
              </p>
            </>
          )}
        </div>
      </PublicLayout>
    );
  }
  // A packet finished earlier (or whose last answer got lost) still routes the
  // signer onward.
  const finishedPurpose = signedPurpose || data?.sessionPurpose || null;

  if (!previewMode && (sessionDone || sessionCompleted)) {
    return (
      <PublicLayout>
        <div className="mx-auto max-w-md py-20 text-center">
          <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-emerald-100 text-emerald-700">
            <Check className="h-7 w-7" />
          </div>
          {underReview ? (
            // Referral partner applicant — their packet just entered the review
            // queue. The personal link + QR arrive by email after approval, so
            // promise nothing here beyond the email.
            <>
              <h1 className="text-xl font-bold text-foreground">
                Paperwork received — we&apos;re on it
              </h1>
              <p className="mt-2 text-muted-foreground">
                Our team is reviewing your application now. Once you&apos;re approved,
                you&apos;ll get an email with your personal referral link and QR code.
              </p>
            </>
          ) : finishedPurpose === 'client_onboarding' ? (
            // A client just signed their Service Agreement - their dashboard
            // is unlocking right now, so send them to it instead of a
            // close-this-page dead end.
            <>
              <h1 className="text-xl font-bold text-foreground">All done. Thank you!</h1>
              <p className="mt-2 text-muted-foreground">
                Your Service Agreement is signed and your client dashboard is
                ready.
              </p>
              <a
                href="/dashboard"
                className="mt-4 inline-flex items-center rounded-lg bg-primary px-5 py-2 text-sm font-semibold !text-primary-foreground !no-underline hover:opacity-90"
              >
                Go to your dashboard
              </a>
            </>
          ) : (
            <>
              <h1 className="text-xl font-bold text-foreground">All done. Thank you!</h1>
              <p className="mt-2 text-muted-foreground">
                Your documents have been signed and submitted. You can close this page.
              </p>
            </>
          )}
        </div>
      </PublicLayout>
    );
  }

  const pageCount = current?.pageCount || 1;
  const pages = Array.from({ length: pageCount }, (_, i) => i + 1);
  const multi = documents.length > 1;

  const surface = (
    <div
      ref={setSurfaceEl}
      className={cn('mx-auto max-w-3xl', guideStep && narrow ? 'pb-[24rem]' : 'pb-32')}
    >
      {/* Compact progress bar that sticks to the top of the viewport (the black
          Veritas banner scrolls away above it). Spans the content column — the
          old vw full-bleed trick (ml-[calc(50%-50vw)] w-screen) caused a
          permanent horizontal scrollbar on Windows. Document + progress on the
          left, zoom control on the right. */}
      {/* Stands apart from the pages (Nic 2026-10-24: it blended into the
          white pages scrolling under it): solid, floating a little below the
          top with a real shadow and a hairline edge, rounded all round. */}
      <div className="sticky top-2 z-30 rounded-xl bg-card shadow-[0_8px_24px_-8px_rgba(15,23,42,0.35)] ring-2 ring-slate-300 dark:ring-white/25">
        <div className="mx-auto flex max-w-3xl items-center gap-3 px-4 py-2">
          <div className="flex min-w-0 flex-1 flex-wrap items-center gap-x-3 gap-y-1">
            {multi ? (
              <span className="flex shrink-0 items-center gap-2 text-xs font-medium text-muted-foreground">
                <span>
                  Document {currentIndex + 1} of {documents.length}
                  {current?.name ? <span className="text-foreground"> · {current.name}</span> : null}
                </span>
                {/* Step dots: jump back to any already-visited doc. Forward past
                    the furthest-reached doc is not allowed. */}
                <span className="flex items-center" role="group" aria-label="Document steps">
                  {documents.map((d, i) => {
                    const visited = i <= maxVisitedIndex;
                    const done = completedSet.has(d.envelopeId);
                    return (
                      <button
                        key={d.envelopeId}
                        type="button"
                        onClick={() => goToDocument(i)}
                        disabled={!visited || i === currentIndex}
                        aria-label={`Go to document ${i + 1}${d.name ? `: ${d.name}` : ''}`}
                        aria-current={i === currentIndex ? 'step' : undefined}
                        title={d.name || `Document ${i + 1}`}
                        // p-1 pads the 10px dot into a usable touch target.
                        className={cn('group p-1', !visited && 'cursor-default')}
                      >
                        <span
                          className={cn(
                            'block h-2.5 w-2.5 rounded-full transition-shadow',
                            i === currentIndex
                              ? 'bg-primary ring-2 ring-primary/30'
                              : done
                                ? 'bg-emerald-500 group-hover:ring-2 group-hover:ring-emerald-300'
                                : visited
                                  ? 'bg-muted-foreground/50 group-hover:ring-2 group-hover:ring-muted-foreground/30'
                                  : 'bg-muted-foreground/20',
                          )}
                        />
                      </button>
                    );
                  })}
                </span>
              </span>
            ) : null}
            {currentDocDone ? (
              <span className="inline-flex items-center gap-1 whitespace-nowrap rounded-full bg-emerald-100 px-2.5 py-0.5 text-xs font-semibold text-emerald-800 sm:gap-1.5 sm:px-3 sm:py-1 sm:text-sm">
                <Check className="h-3.5 w-3.5 sm:h-4 sm:w-4" /> {isLastRemaining ? 'Ready to submit' : "You're good to continue!"}
              </span>
            ) : (
              <div className="flex flex-wrap items-center gap-2">
                <StatBanner label="Required" done={requiredDone} total={requiredItems.length} complete={allRequiredDone} />
                <StatBanner label="Signatures" done={sigDone} total={signatureFields.length} complete={allSigDone} />
                {/* Where to go next (Nic 2026-10-15): Start on a fresh
                    document, then Next field to the next open one. */}
                {/* Phones too (Nic 2026-10-15): Start opens the wizard, which
                    is the only way to fill on a phone. */}
                {firstOpen != null && ready && !pdfError && !(narrow && guideStep) ? (
                  <button
                    type="button"
                    onClick={goNextField}
                    className="inline-flex items-center gap-1 whitespace-nowrap rounded-full bg-primary px-3 py-1 text-xs font-semibold text-primary-foreground shadow-sm hover:opacity-90 sm:text-sm"
                  >
                    {guideIdx == null && requiredDone + sigDone === 0 ? 'Start' : 'Next field'}
                    <ArrowRight className="h-3.5 w-3.5" />
                  </button>
                ) : null}
                {/* What the current box is for (Nic 2026-10-24): boxes on the
                    page stay empty, no placeholder text, so its name shows
                    here instead. Phones show it in the card. */}
                {guideStep && !narrow ? (
                  <span className="inline-flex min-w-0 max-w-[18rem] items-center gap-1 text-xs text-muted-foreground sm:text-sm">
                    <span className="shrink-0">{verbOf(guideStep)}:</span>
                    <span className="truncate font-semibold text-foreground">{guideStep.label}</span>
                    {!guideStep.required ? <span className="shrink-0">(optional)</span> : null}
                  </span>
                ) : null}
              </div>
            )}
          </div>
          {/* Zoom control, to the right of everything. Hidden on phones
              (Nic 2026-10-15: it sat on top of Start): they pinch-zoom, and the
              pages are drawn sharp enough for it. */}
          <div className="hidden shrink-0 items-center gap-1.5 rounded-full border border-border bg-card px-2.5 py-1 shadow-sm md:flex">
            <button type="button" onClick={() => setZoom((z) => Math.max(ZOOM_MIN, +(z - ZOOM_STEP).toFixed(2)))} disabled={zoom <= ZOOM_MIN} className="rounded-full p-1 text-foreground hover:bg-accent disabled:opacity-40" aria-label="Zoom out">
              <ZoomOut className="h-4 w-4" />
            </button>
            <span className="w-10 text-center text-xs font-medium text-muted-foreground">{Math.round(zoom * 100)}%</span>
            <button type="button" onClick={() => setZoom((z) => Math.min(ZOOM_MAX, +(z + ZOOM_STEP).toFixed(2)))} disabled={zoom >= ZOOM_MAX} className="rounded-full p-1 text-foreground hover:bg-accent disabled:opacity-40" aria-label="Zoom in">
              <ZoomIn className="h-4 w-4" />
            </button>
          </div>
        </div>
      </div>

      <style>
        {'@keyframes vr-guide-nudge{0%,100%{transform:translateX(-3px)}50%{transform:translateX(3px)}}'}
      </style>
      <div className="py-4">
        <h1 className="text-xl font-bold text-foreground">{current?.name}</h1>
        {current?.description ? (
          <p className="text-sm text-muted-foreground">{current.description}</p>
        ) : null}
      </div>

      <div className="space-y-6">
        {pages.map((pageNum) => (
          // Two display modes for a zoomed page (see zoomFitsViewport): while it
          // fits the viewport, the card just grows and stays centered (allowed
          // to overflow the column — the natural zoom feel). Once wider than
          // the viewport, the row becomes a LEFT-anchored horizontal scroller —
          // justify-center there would clip the left half unreachably (the
          // original phone bug). py-1 keeps the card shadow unclipped.
          <div
            key={`${envId}-${pageNum}`}
            className={zoomFitsViewport ? 'flex justify-center py-1' : 'overflow-x-auto py-1'}
          >
            <div
              className="relative mx-auto w-max rounded-md bg-white shadow-md ring-1 ring-black/5"
              onFocus={onPageFocus}
              style={
                !ready || !pageImages[pageNum]
                  ? { width: displayWidth, height: placeholderHeight(displayWidth) }
                  : undefined
              }
            >
              {pageImages[pageNum] ? (
                <img
                  src={pageImages[pageNum].url}
                  alt=""
                  draggable={false}
                  // max-w-none: the base style caps every image at its column,
                  // which squashed a zoomed page sideways (Nic 2026-10-15).
                  className={cn('block max-w-none select-none', !ready && 'invisible')}
                  style={{
                    width: displayWidth,
                    height:
                      (displayWidth * pageImages[pageNum].height) / pageImages[pageNum].width,
                  }}
                />
              ) : null}
              {pdfError ? (
                <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 rounded-md bg-neutral-100 px-6 text-center">
                  <p className="mb-0 text-sm font-medium text-neutral-700">{pdfError}</p>
                  <button
                    type="button"
                    onClick={reloadPdf}
                    className="inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground hover:opacity-90"
                  >
                    Reload document
                  </button>
                </div>
              ) : !ready || !pageImages[pageNum] ? (
                // This page is still being drawn (the ones above already show).
                <div className="absolute inset-0 flex items-center justify-center rounded-md bg-neutral-100">
                  <Loader2 className="h-6 w-6 animate-spin text-neutral-400" />
                </div>
              ) : (
                (() => {
                  // Reading order (Nic 2026-10-24): Tab moves top to bottom,
                  // left to right, like the guide, not in the builder's order.
                  const pageFields = placements
                    .filter((p) => (p.page || 1) === pageNum)
                    .sort(readingOrder);
                  const renderField = (p) => (
                    <SignField
                      key={p.fieldId}
                      p={p}
                      value={values[p.fieldId]}
                      onChange={(v) => setValue(p.fieldId, v)}
                      onOpenSignature={() => setSigField(p.fieldId)}
                      onCheckable={(v) => onCheckableChange(p, v)}
                      disabled={!fieldEnabled(p, values)}
                      required={effectiveRequired(p)}
                      scale={pageScale}
                      registerInput={(el) => {
                        if (el) inputsRef.current[p.fieldId] = el;
                        else delete inputsRef.current[p.fieldId];
                      }}
                      compact={narrow}
                      active={activeIds.has(p.fieldId)}
                      onOpenGuide={openGuideAtField}
                      onAutoAdvance={() => {
                        const nextId = nextFocusableFieldId(placements, p.fieldId);
                        const el = nextId && inputsRef.current[nextId];
                        if (el) el.focus();
                      }}
                    />
                  );
                  // Wrap each radio group in role="radiogroup" for screen readers.
                  // display:contents keeps the members' absolute positioning
                  // relative to the page container (the wrapper adds no box).
                  const renderedGroups = new Set();
                  // "You are here" (Nic 2026-10-24, was a red arrow beside the
                  // field that could sit on the printed words): like DocuSign,
                  // a tag OUTSIDE the page in the left margin, level with the
                  // current field, saying what to do there (Fill in, Sign,
                  // Check, Choose); clicking it goes to that field. With no
                  // room beside the page (phones, a zoomed page), a slim
                  // see-through tab on the paper's very edge, its blank
                  // margin, so it never covers a word.
                  const anchor = guideStep
                    ? pageFields.find((f) => f.fieldId === guideStep.anchorId)
                    : null;
                  const roomBeside =
                    !narrow && zoomFitsViewport && (viewportW - displayWidth) / 2 >= 116;
                  const midY = anchor ? `${(anchor.y + anchor.h / 2) * 100}%` : null;
                  let navigator = null;
                  if (anchor && roomBeside) {
                    navigator = (
                      <button
                        key="__guide-tag"
                        type="button"
                        onClick={() => goToStep(guideStep)}
                        aria-label={`${verbOf(guideStep)}: ${guideStep.label}`}
                        className="vr-guide-tag absolute z-20 flex h-[30px] items-center gap-1 whitespace-nowrap border-0 bg-amber-400 pl-3 pr-[18px] text-[13px] font-bold text-slate-900 outline-none hover:bg-amber-300 focus-visible:bg-amber-300 motion-safe:animate-[vr-guide-nudge_1.1s_ease-in-out_infinite]"
                        style={{
                          right: 'calc(100% + 10px)',
                          top: `calc(${midY} - 15px)`,
                          clipPath:
                            'polygon(0 0, calc(100% - 12px) 0, 100% 50%, calc(100% - 12px) 100%, 0 100%)',
                          borderRadius: '6px 0 0 6px',
                        }}
                      >
                        {verbOf(guideStep)}
                      </button>
                    );
                  } else if (anchor) {
                    navigator = (
                      <span
                        key="__guide-tab"
                        aria-hidden
                        className="pointer-events-none absolute left-0 z-20 flex h-[26px] w-[14px] items-center justify-center rounded-r-md bg-amber-500/70 text-white motion-safe:animate-[vr-guide-nudge_1.1s_ease-in-out_infinite]"
                        style={{ top: `calc(${midY} - 13px)` }}
                      >
                        <ChevronRight className="h-3.5 w-3.5" strokeWidth={3} />
                      </span>
                    );
                  }
                  return [navigator].concat(pageFields.map((p) => {
                    if (p.type === 'radio' && p.group) {
                      if (renderedGroups.has(p.group)) return null;
                      renderedGroups.add(p.group);
                      return (
                        <div
                          key={`radiogroup-${p.group}`}
                          role="radiogroup"
                          aria-label={p.group}
                          className="contents"
                        >
                          {pageFields
                            .filter((q) => q.type === 'radio' && q.group === p.group)
                            .map(renderField)}
                        </div>
                      );
                    }
                    return renderField(p);
                  }));
                })()
              )}
            </div>
          </div>
        ))}
      </div>

    </div>
  );

  // Phones (Nic 2026-10-15): no bottom bar while fields are left; Start in
  // the top bar opens the card. Once the document is filled the bar comes
  // back with Continue (or the consent and Finish & Submit).
  const hideBar = narrow && !guideStep && !currentDocDone && !previewMode;
  const bottomBar = hideBar ? null : (
    // pb-[env(...)] keeps the bar's contents above the iOS home indicator.
    <div
      className="fixed inset-x-0 bottom-0 z-[1040] border-t border-border bg-card/95 pb-[env(safe-area-inset-bottom)] backdrop-blur"
    >
      {/* Phones fill through the guide card (Nic 2026-10-15); Done or
          Close brings the usual bar back. */}
      {narrow && guideStep ? (
        <GuideCard
          step={guideStep}
          index={guideIdx}
          total={steps.length}
          values={values}
          setValue={setValue}
          onCheckable={onCheckableChange}
          onOpenSignature={(fieldId) => setSigField(fieldId)}
          fitsBox={fitsBox}
          onBack={() => setGuideIdx((i) => Math.max(0, (i || 0) - 1))}
          onNext={guideNext}
          onClose={() => setGuideIdx(null)}
          isLast={guideIdx >= steps.length - 1}
        />
      ) : (
      <div className="mx-auto flex max-w-3xl flex-col gap-3 px-4 py-3 sm:flex-row sm:items-center">
        {previewMode ? (
          <div className="flex flex-1 items-center justify-end gap-1.5 text-xs font-medium text-muted-foreground">
            <Eye className="h-3.5 w-3.5" /> Preview — submission disabled
          </div>
        ) : (
          <>
            {isLastRemaining ? (
              <label className="flex flex-1 items-start gap-2 text-xs text-muted-foreground">
                <input type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)} className="mt-0.5" />
                <span className="flex flex-col gap-0.5">
                  <span className="flex items-center gap-1">
                    <ShieldCheck className="hidden h-3.5 w-3.5 sm:inline" />
                    I agree to sign electronically and that my electronic signature is legally binding.
                  </span>
                  <span className="text-[11px] leading-snug text-muted-foreground/80">
                    You may request a paper copy of these records, or withdraw your consent, at any time by
                    contacting us at support@veritasratings.com. To open and keep a copy of these documents
                    you&apos;ll need a current web browser and a PDF reader.
                  </span>
                </span>
              </label>
            ) : (
              <div className="flex-1" />
            )}
            <div className="flex items-center justify-end gap-2">
              {currentIndex > 0 ? (
                <button
                  type="button"
                  onClick={() => goToDocument(currentIndex - 1)}
                  disabled={submit.isLoading}
                  className="inline-flex items-center justify-center rounded-md border border-input bg-card px-4 py-2 text-sm font-medium text-foreground hover:bg-accent disabled:opacity-50"
                >
                  Back
                </button>
              ) : null}
              <button
                type="button"
                onClick={isLastRemaining ? handleFinish : handleContinue}
                disabled={isLastRemaining ? !canFinish : !canContinue}
                className="inline-flex items-center justify-center gap-2 rounded-md bg-primary px-5 py-2 text-sm font-semibold text-primary-foreground hover:opacity-90 disabled:opacity-50"
              >
                {submit.isLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                {isLastRemaining ? 'Finish & Submit' : 'Continue'}
              </button>
            </div>
          </>
        )}
      </div>
      )}
      {submitError ? (
        <div className="mx-auto max-w-3xl px-4 pb-3">
          {/* A dead link (voided, replaced by a re-send, or expired) is not
              the signer's mistake - swap the raw error for an explanation
              with an exit: the newest text/email link works, or signing in
              routes them to onboarding where the agreement reopens. */}
          {/no longer available|invalid|expired|replaced/i.test(String(submitError)) ? (
            <div
              role="alert"
              className="rounded-md bg-status-error-bg px-3 py-2.5 text-sm text-status-error-fg"
            >
              <div className="font-semibold">
                {submitError === REPLACED_MSG ? REPLACED_MSG : 'This signing link is no longer active.'}
              </div>
              <div className="mt-1">
                You can return to your onboarding page to open the newest
                Service Agreement.
              </div>
              <a
                href="/sign-in"
                className="mt-2 inline-flex items-center rounded-lg bg-white px-3.5 py-1.5 text-sm font-semibold text-slate-700 shadow-sm ring-1 ring-slate-200 hover:bg-slate-50"
              >
                Return to onboarding
              </a>
            </div>
          ) : (
            <div
              role="alert"
              className="rounded-md bg-status-error-bg px-3 py-2 text-sm font-medium text-status-error-fg"
            >
              {submitError}
            </div>
          )}
        </div>
      ) : null}
    </div>
  );

  const signatureModal = sigField ? (
    <SignatureModal
      // Reopen in the same mode it was captured (e.g. Type), but BLANK — a fresh
      // canvas/input to re-sign, not the previous signature pre-filled.
      initialMode={sigMetaRef.current[sigField]?.mode || 'draw'}
      onClose={() => setSigField(null)}
      onApply={(dataUrl, meta) => {
        // Desktop: the tag moves on once they've signed (effect above).
        advanceAfterSignRef.current = !narrow;
        setValue(sigField, dataUrl);
        if (meta) sigMetaRef.current[sigField] = meta;
        setSigField(null);
      }}
    />
  ) : null;

  if (previewMode) {
    return (
      <div className="fixed inset-0 z-[1100] overflow-auto bg-background">
        <button type="button" onClick={onClosePreview} className="fixed right-4 top-4 z-[1110] inline-flex items-center gap-1.5 rounded-full border border-border bg-card px-3 py-1.5 text-xs font-medium text-foreground shadow-md hover:bg-accent">
          <X className="h-3.5 w-3.5" /> Exit preview
        </button>
        <PublicLayout>
          <SignerErrorBoundary>
            {surface}
            {bottomBar}
            {signatureModal}
          </SignerErrorBoundary>
        </PublicLayout>
      </div>
    );
  }

  return (
    <PublicLayout>
      <SignerErrorBoundary>
        {surface}
        {bottomBar}
        {signatureModal}
      </SignerErrorBoundary>
    </PublicLayout>
  );
};

// The page itself can't catch its own crash, so it sits inside one too.
const SignDocumentPage = (props) => (
  <SignerErrorBoundary withLayout={!props.previewMode}>
    <SignDocument {...props} />
  </SignerErrorBoundary>
);

export default SignDocumentPage;
